import { Button, Form, Input, message, Modal, Radio, Select } from "antd";
import { useFormik } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import * as yup from "yup";
import addIcon from "../../assets/icons/add-color.svg";
import { getBeneficiary } from "../../actions/beneficiary/beneficiary";
import { useDispatch, useSelector } from "react-redux";
import bankIcon from "../../assets/icons/bank.svg";
import transit from "../../assets/images/expense.svg";
import { cardsTransfer } from "../../actions/cards/cards";
import TextArea from "antd/lib/input/TextArea";
import { ClipLoader } from "react-spinners";
import usePayAxios from "../../config/useAxios";
import { apis } from "../../config/APIs";

const formatTime = (time) => {
  const minutes = Math.floor(time / 60);
  const seconds = time % 60;
  return `${minutes}:${seconds < 10 ? "0" : ""}${seconds}`;
};

const TransferModal = ({
  cardData,
  walletData,
  isTransfer,
  setIsTransfer,
  setAddBeneficiary,
}) => {
  const dispatch = useDispatch();
  const { onCall: verifyOtpCall, loading: verifyLoading } = usePayAxios({
    api: "/api/cards/v1/verifyOtp",
    method: "post",
  });

  const {
    onCall: sendOtpCall,
    data,
    loading,
    reset,
  } = usePayAxios({
    api: apis.send_otp,
    method: "post",
  });

  const [state, setState] = useState({
    timeLeft: 120,
    resendMessage: false,
  });

  const [sendOtp, setSendOtp] = React.useState(false);
  const [otp, setOtp] = React.useState("");
  const beneficiaryData = useSelector(
    (state) =>
      state?.beneficiary?.beneficiaryData?.data?.response?.beneficiaries,
  );
  const cardTransferCreateData = useSelector(
    (state) => state?.cards?.cardsTransferData?.data,
  );
  const cardTransferCreateLoading = useSelector(
    (state) => state?.cards?.cardsTransferData?.loading,
  );

  const ValidationSchema = yup.object().shape({
    transfer_from: yup
      .string()
      .nullable()
      .required("Transfer from is required"),
    transfer_to: yup.string().nullable().required("Transfer to is required"),
    amount: yup
      .number()
      .typeError("Amount must be a number")
      .required("Amount is required")
      .integer("Amount must be a Positive number")
      .min(1, "Amount must be greater then 0"),
    remarks: yup.string().when("transfer_to", {
      is: (val) => val && val.toLowerCase() !== "transit",
      then: yup
        .string()
        .required("Remarks is required")
        .max(50, "Remarks must not exceed 50 characters"),
      otherwise: yup.string().notRequired(),
    }),
    mode_of_pay: yup.string().when("transfer_to", {
      is: (val) => val && val.toLowerCase() !== "transit",
      then: yup.string().required("Mode of pay is required"),
      otherwise: yup.string().notRequired(),
    }),
  });

  useEffect(() => {
    if (!state.timeLeft) return;
    const intervalId = setInterval(() => {
      setState({ ...state, timeLeft: state.timeLeft - 1 });
    }, 1000);
    return () => clearInterval(intervalId);
  }, [state.timeLeft]);

  React.useEffect(() => {
    if (cardTransferCreateData?.status == true) {
      setOtp("");
      setSendOtp(false);
    } else if (cardTransferCreateData?.status == false) {
      setOtp("");
      setSendOtp(false);
    }
  }, [cardTransferCreateData]);

  useEffect(() => {
    if (isTransfer?.type === "transit_wallet") {
      formik.setFieldValue("transfer_to", "TRANSIT");
    }
  }, [isTransfer?.type]);

  const formik = useFormik({
    initialValues: {
      transfer_from: "PERSONAL",
      transfer_to: null,
      mode_of_pay: "IMPS",
      amount: "",
      remarks: "",
    },
    validationSchema: ValidationSchema,
    onSubmit: (values) => {
      const isTransitTransfer =
        values?.transfer_to?.toLowerCase() === "transit" ||
        isTransfer?.type === "transit_wallet";

      if (isTransitTransfer) {
        delete values?.mode_of_pay;
        delete values?.remarks;
        dispatch(cardsTransfer({ ...values, card_id: cardData?.id }));
        return;
      }
      if (sendOtp) {
        verifyOtpCall({
          data: {
            otp: otp,
            card_id: cardData?.id,
            txnId: sendOtp?.txnId,
            action_type: `OTP_FOR_${values?.mode_of_pay}`,
          },
        })
          .then((res) => {
            if (res?.status) {
              dispatch(cardsTransfer({ ...values, card_id: cardData?.id }));
            }
          })
          .catch(() => {
            message.error(
              <span className="messageText">Failed to verify otp</span>,
            );
            setOtp("");
          });
      } else {
        sendOtpCall({
          data: {
            action_type: `OTP_FOR_${values?.mode_of_pay}`,
            alert_type: "SMS",
            card_id: cardData?.id,
            transaction_amount: values?.amount,
          },
        })
          .then((res) => {
            if (res?.status) {
              setSendOtp(res?.response);
              const expiryTime = Number.isFinite(
                Number(res?.response?.otpExpiryInMinutes)
              )
                ? Number(res?.response?.otpExpiryInMinutes) * 60
                : 120;
              setState({
                ...state,
                timeLeft: expiryTime,
                resendMessage: false,
              });
            }
          })
          .catch(() => {
            message.error(
              <span className="messageText">
                Amount must be a valid number with up to 5 digits and no decimal
                places
              </span>,
            );
          });
      }
    },
  });

  useEffect(() => {
    dispatch(getBeneficiary({ pagination: false }));
  }, []);

  const formatBeneficiaryOptions = useMemo(() => {
    if (beneficiaryData?.length > 0) {
      return beneficiaryData?.map((beneficiary) => {
        const accountNumber = beneficiary?.bank_account_details?.account_number;
        const maskedAccountNumber = accountNumber
          ? `XXXXX${accountNumber.slice(-5)}`
          : "XXXXX";

        return {
          label: (
            <div className="d-flex">
              <img
                src={bankIcon}
                alt="bank-icon"
                width={15}
                className="mrHalf"
              />
              <span style={{ color: "var(--text-primary)" }}>
                {beneficiary?.beneficiary_name} - {maskedAccountNumber}
              </span>
            </div>
          ),
          value: beneficiary.id,
        };
      });
    } else {
      return [];
    }
  }, [beneficiaryData]);

  const transitWallet = useMemo(() => {
    if (walletData?.length > 0) {
      return walletData
        ?.filter(({ walletName }) =>
          ["transit"].includes(walletName?.toLowerCase()),
        )
        .map(({ walletName }) => ({
          label: (
            <div className="d-flex">
              <img
                src={transit}
                alt="bank-icon"
                width={15}
                className="mrHalf"
              />
              <span style={{ color: "var(--text-primary)" }}>{walletName}</span>
            </div>
          ),
          value: walletName,
        }));
    } else {
      return [];
    }
  }, [walletData]);

  const options = useMemo(() => {
    let originalOptions = [
      {
        label: (
          <div
            className="add-btn d-flex"
            onClick={() => {
              setIsTransfer({ open: false, type: "" });
              setAddBeneficiary(true);
            }}
          >
            <img
              src={addIcon}
              alt="add-beneficiary"
              width={15}
              className="mrHalf"
            />
            <span>Add Beneficiary</span>
          </div>
        ),
        title: "add-beneficiary",
        options: [],
      },
      {
        label: <span>WALLETS</span>,
        title: "wallets",
        options: [...transitWallet],
      },
      {
        label: <span>BENEFICIARIES</span>,
        title: "beneficiarys",
        options: [...formatBeneficiaryOptions],
      },
    ];

    if (isTransfer?.type == "transit_wallet") {
      return [
        {
          label: <span>WALLETS</span>,
          title: "wallets",
          options: [...transitWallet],
        },
      ];
    } else if (isTransfer?.type == "bank_account") {
      return [
        {
          label: (
            <div
              className="add-btn d-flex"
              onClick={() => {
                setIsTransfer({ open: false, type: "" });
                setAddBeneficiary(true);
              }}
            >
              <img
                src={addIcon}
                alt="add-beneficiary"
                width={15}
                className="mrHalf"
              />
              <span>Add Beneficiary</span>
            </div>
          ),
          title: "add-beneficiary",
          options: [],
        },

        {
          label: <span>BENEFICIARIES</span>,
          title: "beneficiarys",
          options: [...formatBeneficiaryOptions],
        },
      ];
    }
  }, [isTransfer?.type, beneficiaryData]);

  return (
    <>
      <Modal
        title={`${
          isTransfer?.type == "transit_wallet"
            ? "Transfer to Transit Wallet"
            : "Transfer to Bank Account"
        }`}
        open={isTransfer?.open}
        onCancel={() => {
          setIsTransfer({ open: false, type: "" });
        }}
        footer={[]}
        className="right-alinged-modal"
      >
        <Form
          onFinish={formik.handleSubmit}
          layout="vertical"
          className="loginFormDiv"
        >
          {sendOtp?.txnId ? (
            <>
              <Form.Item label="Verify OTP" required>
                <Input
                  placeholder="Verify OTP"
                  value={otp}
                  onChange={(e) => {
                    if (Number.isNaN(Number(e.target.value))) {
                      return;
                    }
                    setOtp(e.target.value);
                  }}
                />
              </Form.Item>
              <div className="timeLeft">
                {state.timeLeft > 0 && (
                  <div
                    className="alignCenter"
                    style={{ color: "var(--text-primary)" }}
                  >
                    OTP expires in{" "}
                    {state.timeLeft > 120
                      ? formatTime(state.timeLeft)
                      : `${state.timeLeft} seconds`}
                  </div>
                )}
                {state.timeLeft === 0 && (
                  <h5
                    className="otpMsg alignCenter mt-2 text-center mb-1"
                    style={{ color: "var(--text-primary)" }}
                    onClick={() => {
                      sendOtpCall({
                        data: {
                          action_type: `OTP_FOR_${formik.values?.mode_of_pay}`,
                          alert_type: "SMS",
                          card_id: cardData?.id,
                          transaction_amount: formik.values?.amount,
                        },
                      })
                        .then((res) => {
                          if (res?.status) {
                            setSendOtp(res?.response);
                            let timeout = setTimeout(() => {
                              reset();
                              clearTimeout(timeout);
                            }, 6000);
                            const expiryTime = Number.isFinite(
                              Number(res?.response?.otpExpiryInMinutes)
                            )
                              ? Number(res?.response?.otpExpiryInMinutes) * 60
                              : 120;
                            setState({
                              ...state,
                              timeLeft: expiryTime,
                              resendMessage: true,
                            });
                          }
                        })
                        .catch(() => {
                          let timeout = setTimeout(() => {
                            reset();
                            clearTimeout(timeout);
                          }, 6000);
                        });
                    }}
                  >
                    Didn't receive OTP?
                    <span className="Cursor resendOtp  emp-text">
                      Resend OTP
                    </span>
                  </h5>
                )}
                {state.resendMessage &&
                  sendOtp?.txnId &&
                  data?.response?.txnId && (
                    <h4
                      className="text-center"
                      style={{ color: "var(--text-primary)" }}
                    >
                      OTP sent successfully
                    </h4>
                  )}

                {state.timeLeft === 0 && (
                  <h4
                    className="alignCenter text-center"
                    style={{ color: "var(--text-primary)" }}
                  >
                    OTP Expired
                  </h4>
                )}
              </div>
              <Form.Item className="loginButtonsDiv">
                <Button
                  key="1"
                  className="formButton mt1"
                  htmlType="submit"
                  disabled={verifyLoading || cardTransferCreateLoading}
                >
                  {verifyLoading || cardTransferCreateLoading ? (
                    <ClipLoader
                      loading={verifyLoading || cardTransferCreateLoading}
                      size={15}
                      color="white"
                    />
                  ) : (
                    "Verify OTP"
                  )}
                </Button>
                <Button
                  key="2"
                  className="cancelButton ml1 mt1"
                  onClick={() => {
                    setSendOtp(false);
                    setOtp("");
                  }}
                >
                  Back
                </Button>
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item label="Transfer from" name="transfer_from" required>
                <Select
                  name="transfer_from"
                  placeholder="Select Transfer from"
                  value={formik.values.transfer_from}
                  onChange={(val) => formik.setFieldValue("transfer_from", val)}
                  disabled
                >
                  {walletData
                    ?.filter(
                      ({ walletName, sourceWalletTransfer }) =>
                        sourceWalletTransfer === "Y" &&
                        walletName === "PERSONAL",
                    )
                    .map(({ walletName }) => (
                      <Select.Option key={walletName} value={walletName}>
                        {walletName} {"WALLET"}
                      </Select.Option>
                    ))}
                </Select>
                {formik.touched.transfer_from &&
                  formik.errors.transfer_from && (
                    <div className="error">{formik.errors.transfer_from}</div>
                  )}
              </Form.Item>

              <Form.Item label="Transfer to" name="transfer_to" required>
                <Select
                  name="transfer_to"
                  placeholder="Select Transfer to"
                  value={formik.values["transfer_to"]}
                  onChange={(val) => {
                    formik.setFieldValue("transfer_to", val);
                  }}
                  options={[...options]}
                  disabled={isTransfer?.type == "transit_wallet" ? true : false}
                />
                {formik.touched.transfer_to && formik.errors.transfer_to && (
                  <div className="error">{formik.errors.transfer_to}</div>
                )}
              </Form.Item>
              {isTransfer?.type == "bank_account" ? (
                <Form.Item label="" name="mode_of_pay">
                  <Radio.Group
                    onChange={(e) => {
                      formik.setFieldValue("mode_of_pay", e?.target?.value);
                    }}
                    value={formik.values["mode_of_pay"]}
                    defaultValue={"IMPS"}
                  >
                    <Radio value={"IMPS"}>
                      <span style={{ color: "rgba(0, 0, 0, 0.85)" }}>IMPS</span>
                    </Radio>
                    <Radio value={"NEFT"}>
                      <span style={{ color: "rgba(0, 0, 0, 0.85)" }}>NEFT</span>
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              ) : null}

              <Form.Item label="Amount" name="amount" required>
                <Input
                  placeholder="Enter Amount"
                  name="amount"
                  onChange={formik.handleChange}
                  value={formik.values.amount}
                />
                {formik.touched.amount && formik.errors.amount && (
                  <div className="error">{formik.errors.amount}</div>
                )}
                <p
                  style={{
                    color: "#3e3e3e",
                    opacity: 0.8,
                    fontStyle: "italic",
                    margin: "10px 0",
                  }}
                >
                  ( The maximum limit of the transit wallet is INR 2000 at any point of time )
                </p>
              </Form.Item>

              {isTransfer?.type == "bank_account" ? (
                <Form.Item label="Remarks" name="remarks" required>
                  <TextArea
                    onChange={(e) => {
                      formik.setFieldValue("remarks", e?.target?.value);
                    }}
                    placeholder="Enter remarks"
                    value={formik?.values?.remarks}
                  />
                  {formik.touched.remarks && formik.errors.remarks && (
                    <div className="error">{formik.errors.remarks}</div>
                  )}
                </Form.Item>
              ) : null}

              <Form.Item className="loginButtonsDiv">
                <Button
                  key="1"
                  className="formButton mt1"
                  htmlType="submit"
                  disabled={cardTransferCreateLoading || loading}
                >
                  {cardTransferCreateLoading || loading ? (
                    <ClipLoader
                      loading={cardTransferCreateLoading || loading}
                      size={15}
                      color="white"
                    />
                  ) : (
                    "Continue"
                  )}
                </Button>
                <Button
                  key="2"
                  className="cancelButton ml1 mt1"
                  onClick={() => {
                    setIsTransfer({ open: false, type: "" });
                  }}
                >
                  Cancel
                </Button>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </>
  );
};

export default TransferModal;